import { cx } from '@/utils/cx';

import { GripVertical } from './grip-vertical';

export interface DragHandleProps {
  /**
   * Props to spread on the handle button — typically the merged
   * `{ ...listeners, ...attributes }` from `useSortable()` / `useDraggable()`.
   */
  dragHandleProps?: Record<string, unknown>;
  /** Accessible label. Defaults to "Drag to reorder". */
  label?: string;
  /** Additional className on the button. */
  className?: string;
}

/**
 * DragHandle — a standalone grip-icon button for drag-and-drop affordances.
 *
 * A layout primitive: it renders the grip icon, cursor, focus ring, and
 * touch-none. It does NOT call any dnd-kit hook — the consumer wires up
 * their drag library and passes the handle props through.
 *
 * Use this inside table rows, inline layouts, or anywhere a drag handle
 * needs to sit alongside other content without a full DraggableItem wrapper.
 */
export function DragHandle({
  dragHandleProps,
  label = 'Drag to reorder',
  className,
}: DragHandleProps) {
  return (
    <button
      type='button'
      aria-label={label}
      className={cx(
        'inline-flex shrink-0 cursor-grab touch-none items-center',
        'text-fg-quaternary hover:text-fg-tertiary',
        'transition-colors duration-100 active:cursor-grabbing',
        'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-focus-ring',
        className
      )}
      {...dragHandleProps}
    >
      <GripVertical className='size-4' aria-hidden />
    </button>
  );
}
